import React, { useState } from "react";

const Panel = ({
  title,
  subtitle,
  image,
  reverse = false,
  linkText,
  detail,
}) => {
  const [open, setOpen] = useState(false);

  const defaultDetail =
    "Our Business Conduct Policy sets out our ethical requirements for our employees. Each of our employees must certify that they have read and understand the policy when they join FOZZIL, and again each year. Violations of the policy are taken seriously and may result in disciplinary action.";

  return (
    <div className="max-w-6xl mx-auto px-6 py-6 relative">
      {!open ? (
        <div
          className={`flex flex-col md:flex-row items-stretch gap-6 rounded-[28px] overflow-hidden border border-[#22282b] shadow-[0_6px_30px_rgba(0,0,0,0.6)] bg-transparent`}
        >
          <div
            className={`w-full md:w-1/2 flex flex-col justify-between p-12 md:p-14 bg-[#f3f4f6] text-black ${
              reverse ? "order-2 md:order-1" : "order-1"
            }`}
            style={{ minHeight: 360 }}
          >
            <div>
              <h2 className="font-primary text-4xl md:text-5xl mb-4 leading-tight">
                {title}
              </h2>
              <p className="text-lg md:text-xl text-[#374151] mb-6">
                {subtitle}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setOpen(true)}
                className="flex items-center gap-3 text-sm font-semibold text-[#111]"
                aria-expanded={open}
              >
                <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center text-xl">
                  +
                </span>
                <span className="ml-3 text-base">{linkText || "More"}</span>
              </button>
            </div>
          </div>

          <div
            className={`w-full md:w-1/2 ${
              reverse ? "order-1 md:order-2" : "order-2"
            }`}
          >
            <div className="w-full h-full">
              <img
                src={image}
                alt=""
                className="w-full h-[380px] md:h-[420px] object-cover block"
              />
            </div>
          </div>
        </div>
      ) : (
        <div
          className={`flex flex-col md:flex-row items-stretch gap-6 rounded-[28px] overflow-hidden border border-[#22282b] shadow-[0_6px_30px_rgba(0,0,0,0.06)] bg-transparent`}
        >
          <div
            className={`w-full md:w-1/2 flex flex-col justify-between p-12 md:p-14 bg-[#f3f4f6] text-black ${
              reverse ? "order-2 md:order-1" : "order-1"
            }`}
            style={{ minHeight: 360 }}
          >
            <div>
              <h2 className="font-primary text-4xl md:text-5xl mb-4 leading-tight">
                {title}
              </h2>
              <p className="text-lg md:text-xl text-[#374151] mb-6">
                {subtitle}
              </p>
            </div>

            <div className="flex items-center">
              <button
                onClick={() => setOpen(false)}
                className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-[#111] shadow-sm"
                aria-label={`Close ${title}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15 9L9 15"
                    stroke="#111"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 9L15 15"
                    stroke="#111"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div
            className={`w-full md:w-1/2 ${
              reverse ? "order-1 md:order-2" : "order-2"
            }`}
          >
            <div className="w-full h-full">
              <div className="w-full h-[380px] md:h-[420px] bg-[#eceef2] p-8 md:p-10 flex items-start justify-center">
                <div className="max-w-md text-[#111827] leading-relaxed text-base md:text-lg">
                  {detail ? detail : <p>{defaultDetail}</p>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const EthicsAndCompliance = () => {
  const pdfUrl =
    "https://img1.wsimg.com/blobby/go/b0b3e65e-c760-4844-b125-253596bb7178/Business%20Conduct%20of%20FOZZIL-ac4888a.pdf";

  return (
    <div className="bg-white text-black">
      <div className="max-w-4xl mx-auto px-6 pt-16 pb-6 text-center">
        <h1 className="font-primary text-4xl md:text-5xl mb-4">
          ETHICS AND COMPLIANCE
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Updated on 1st October, 2025
        </p>
        <p className="text-lg text-gray-600 mb-8">
          FOZZIL operates with integrity, ethics, and full legal compliance. How
          we do business matters as much as the solutions we create. We uphold
          strict ethical standards in every decision, ensuring responsible
          innovation.
        </p>
      </div>

      <main className="space-y-12 py-12 bg-white">
        <Panel
          title="Our Business Conduct Policy"
          subtitle="Honesty, Respect, Confidentiality, and Compliance"
          image="/fozzilbusinessconduct.png"
          linkText="More"
          detail={
            <p>
              Our{" "}
              <a
                href={pdfUrl}
                className="underline text-blue-600"
                target="_blank"
                rel="noopener noreferrer"
              >
                Business Conduct Policy (PDF)
              </a>{" "}
              sets out our ethical requirements for our employees. Each of our
              employees must certify that they have read and understand the
              policy when they join FOZZIL, and again each year. Violations of
              the policy are taken seriously and may result in disciplinary
              action.
            </p>
          }
        />

        <Panel
          title="Compliance at FOZZIL"
          subtitle="FOZZIL operates with unwavering integrity, accountability,
               and global readiness."
          image="/fozzilcomplaince.png"
          reverse
          linkText="More"
          detail={
            <p>
              A number of compliance functions are deeply integrated into 
              FOZZIL’s business operations. The FOZZIL Compliance & Ethics 
              Team oversees Ethical AI Governance, Political & Regulatory 
              Transparency, Data Privacy & DPDP Act (India) Compliance, Anti-Corruption & Integrity Standards, 
              and Security & Cyber Compliance — all embedded across every level of 
              the organization through mandatory training, regular audits, and independent 
              oversight, ensuring FOZZIL operates with unwavering integrity, accountability,
               and global readiness.
            </p>
          }
        />

        <Panel
          title="Employee Policies"
          subtitle="Ensuring every team member has the tools, guidance, and confidence to act with 
            purpose and principle."
          image="/employee.png"
          linkText="More"
          detail={
            <p>
            Every FOZZIL employee is required to complete mandatory annual Compliance & Ethics Training, 
            reinforcing our commitment to integrity, transparency, and responsible AI. 
            Additional support is available through the FOZZIL Ethics Helpline, the Compliance Portal, 
            and live Q&A sessions — ensuring every team member has the tools, guidance, and confidence to act with 
            purpose and principle.
            </p>
          }
        />
      </main>
    </div>
  );
};

export const EthicsCompliance = () => null;
